import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO, generateServiceSchema } from '../../utils/seo';
import BookingTracker from './components/BookingTracker';
import AvailabilityCalendar from './components/AvailabilityCalendar';

// Tarifs prédéfinis
const routes: Record<string, Record<string, { distance: number; basePrice: number; nightSurcharge: number }>> = {
  'geneve-aeroport': {
    'chamonix': { distance: 88, basePrice: 250.64, nightSurcharge: 50.13 },
    'megeve': { distance: 72, basePrice: 264.50, nightSurcharge: 52.90 },
    'courchevel': { distance: 180, basePrice: 358.74, nightSurcharge: 71.75 },
    'meribel': { distance: 170, basePrice: 369.09, nightSurcharge: 73.82 },
    'val-thorens': { distance: 190, basePrice: 401.29, nightSurcharge: 80.26 },
    'les-menuires': { distance: 185, basePrice: 494.50, nightSurcharge: 98.90 },
    'val-disere': { distance: 210, basePrice: 423.14, nightSurcharge: 84.63 },
    'tignes': { distance: 215, basePrice: 416.24, nightSurcharge: 83.25 },
    'la-plagne': { distance: 165, basePrice: 437.00, nightSurcharge: 87.40 },
    'les-arcs': { distance: 170, basePrice: 448.50, nightSurcharge: 89.70 },
    'morzine': { distance: 95, basePrice: 243.74, nightSurcharge: 48.75 },
    'annecy': { distance: 45, basePrice: 157.49, nightSurcharge: 31.50 },
    'geneve': { distance: 8, basePrice: 88.49, nightSurcharge: 17.70 },
  },
  'lyon-saint-exupery': {
    'chamonix': { distance: 220, basePrice: 437.00, nightSurcharge: 87.40 },
    'megeve': { distance: 180, basePrice: 402.50, nightSurcharge: 80.50 },
    'courchevel': { distance: 180, basePrice: 484.09, nightSurcharge: 96.82 },
    'meribel': { distance: 170, basePrice: 495.59, nightSurcharge: 99.12 },
    'val-thorens': { distance: 190, basePrice: 518.59, nightSurcharge: 103.72 },
    'val-disere': { distance: 220, basePrice: 553.09, nightSurcharge: 110.62 },
    'tignes': { distance: 230, basePrice: 541.59, nightSurcharge: 108.32 },
    'la-plagne': { distance: 165, basePrice: 472.59, nightSurcharge: 94.52 },
    'les-arcs': { distance: 170, basePrice: 478.34, nightSurcharge: 95.67 },
  },
  'chambery-aeroport': {
    'val-thorens': { distance: 110, basePrice: 393.24, nightSurcharge: 78.65 },
    'tignes': { distance: 135, basePrice: 457.64, nightSurcharge: 91.53 },
    'val-disere': { distance: 130, basePrice: 393.24, nightSurcharge: 78.65 },
    'la-plagne': { distance: 120, basePrice: 377.14, nightSurcharge: 75.43 },
    'courchevel': { distance: 100, basePrice: 323.09, nightSurcharge: 64.62 },
    'lyon-aeroport': { distance: 95, basePrice: 235.69, nightSurcharge: 47.14 },
    'meribel': { distance: 90, basePrice: 328.84, nightSurcharge: 65.77 },
    'saint-bon-tarentaise': { distance: 105, basePrice: 379.44, nightSurcharge: 75.89 },
    'les-arcs': { distance: 115, basePrice: 379.44, nightSurcharge: 75.89 },
    'les-menuires': { distance: 100, basePrice: 357.59, nightSurcharge: 71.52 },
    'la-tania': { distance: 95, basePrice: 336.89, nightSurcharge: 67.38 },
    'les-arcs-1950-2000': { distance: 120, basePrice: 401.29, nightSurcharge: 80.26 },
    'les-deux-alpes': { distance: 125, basePrice: 385.19, nightSurcharge: 77.04 },
    'la-rosiere': { distance: 125, basePrice: 401.29, nightSurcharge: 80.26 },
    'alpe-dhuez': { distance: 130, basePrice: 401.29, nightSurcharge: 80.26 },
    'les-arcs-1600-1800': { distance: 115, basePrice: 379.44, nightSurcharge: 75.89 },
    'val-claret': { distance: 135, basePrice: 401.29, nightSurcharge: 80.26 },
    'les-belleville': { distance: 90, basePrice: 328.84, nightSurcharge: 65.77 },
    'belle-plagne': { distance: 120, basePrice: 393.24, nightSurcharge: 78.65 },
    'morzine': { distance: 180, basePrice: 461.09, nightSurcharge: 92.22 },
  },
  'gare-moutiers': {
    'courchevel': { distance: 28, basePrice: 120, nightSurcharge: 24 },
    'meribel': { distance: 22, basePrice: 100, nightSurcharge: 20 },
    'val-thorens': { distance: 35, basePrice: 140, nightSurcharge: 28 },
    'les-menuires': { distance: 30, basePrice: 130, nightSurcharge: 26 },
  },
  'grenoble-aeroport': {
    'alpe-dhuez': { distance: 65, basePrice: 139.09, nightSurcharge: 27.82 },
    'les-deux-alpes': { distance: 75, basePrice: 150.59, nightSurcharge: 30.12 },
    'chamrousse': { distance: 35, basePrice: 98.84, nightSurcharge: 19.77 },
    'villard-de-lans': { distance: 45, basePrice: 110.34, nightSurcharge: 22.07 },
  },
};

const departureOptions = [
  { value: 'geneve-aeroport', label: '✈️ Aéroport de Genève (GVA)', category: 'Aéroports' },
  { value: 'lyon-saint-exupery', label: '✈️ Aéroport Lyon Saint-Exupéry (LYS)', category: 'Aéroports' },
  { value: 'chambery-aeroport', label: '✈️ Aéroport de Chambéry (CMF)', category: 'Aéroports' },
  { value: 'grenoble-aeroport', label: '✈️ Aéroport de Grenoble (GNB)', category: 'Aéroports' },
  { value: 'annecy-aeroport', label: '✈️ Aéroport d\'Annecy', category: 'Aéroports' },
  { value: 'turin-aeroport', label: '✈️ Aéroport de Turin Caselle (TRN)', category: 'Aéroports' },
  { value: 'milan-malpensa', label: '✈️ Aéroport de Milan Malpensa (MXP)', category: 'Aéroports' },
  { value: 'gare-moutiers', label: '🚆 Gare de Moûtiers', category: 'Gares' },
  { value: 'gare-albertville', label: '🚆 Gare TGV Albertville', category: 'Gares' },
  { value: 'gare-chambery', label: '🚆 Gare de Chambéry', category: 'Gares' },
  { value: 'gare-annecy-tgv', label: '🚆 Gare TGV Annecy', category: 'Gares' },
  { value: 'gare-geneva-cornavin', label: '🚆 Gare de Genève Cornavin', category: 'Gares' },
  { value: 'gare-lyon-part-dieu', label: '🚆 Gare de Lyon Part-Dieu', category: 'Gares' },
  { value: 'gare-lyon-saint-exupery', label: '🚆 Gare de Lyon Saint-Exupéry', category: 'Gares' },
  { value: 'hopital-moutiers', label: '🏥 Hôpital de Moûtiers', category: 'Hôpitaux' },
  { value: 'hopital-albertville', label: '🏥 Hôpital d\'Albertville', category: 'Hôpitaux' },
  { value: 'hopital-bourg-st-maurice', label: '🏥 Hôpital de Bourg St Maurice', category: 'Hôpitaux' },
  { value: 'hopital-chambery', label: '🏥 Hôpital de Chambéry', category: 'Hôpitaux' },
  { value: 'chamonix', label: '⛷️ Chamonix', category: 'Stations de ski' },
  { value: 'megeve', label: '⛷️ Megève', category: 'Stations de ski' },
  { value: 'courchevel', label: '⛷️ Courchevel', category: 'Stations de ski' },
  { value: 'meribel', label: '⛷️ Méribel', category: 'Stations de ski' },
  { value: 'val-thorens', label: '⛷️ Val Thorens', category: 'Stations de ski' },
  { value: 'les-menuires', label: '⛷️ Les Menuires', category: 'Stations de ski' },
  { value: 'val-disere', label: '⛷️ Val d\'Isère', category: 'Stations de ski' },
  { value: 'tignes', label: '⛷️ Tignes', category: 'Stations de ski' },
  { value: 'la-plagne', label: '⛷️ La Plagne', category: 'Stations de ski' },
  { value: 'les-arcs', label: '⛷️ Les Arcs', category: 'Stations de ski' },
  { value: 'morzine', label: '⛷️ Morzine', category: 'Stations de ski' },
  { value: 'saint-bon-tarentaise', label: '⛷️ Saint-Bon-Tarentaise', category: 'Stations de ski' },
  { value: 'la-tania', label: '⛷️ La Tania', category: 'Stations de ski' },
  { value: 'les-arcs-1950-2000', label: '⛷️ Les Arcs (1950/2000)', category: 'Stations de ski' },
  { value: 'les-deux-alpes', label: '⛷️ Les Deux Alpes', category: 'Stations de ski' },
  { value: 'la-rosiere', label: '⛷️ La Rosière', category: 'Stations de ski' },
  { value: 'alpe-dhuez', label: '⛷️ Alpe d\'Huez', category: 'Stations de ski' },
  { value: 'les-arcs-1600-1800', label: '⛷️ Les Arcs (1600/1800)', category: 'Stations de ski' },
  { value: 'val-claret', label: '⛷️ Val Claret', category: 'Stations de ski' },
  { value: 'les-belleville', label: '⛷️ Les Belleville', category: 'Stations de ski' },
  { value: 'belle-plagne', label: '⛷️ Belle Plagne', category: 'Stations de ski' },
  { value: 'chamrousse', label: '⛷️ Chamrousse', category: 'Stations de ski' },
  { value: 'villard-de-lans', label: '⛷️ Villard-de-Lans', category: 'Stations de ski' },
  { value: 'flaine', label: '⛷️ Flaine', category: 'Stations de ski' },
  { value: 'avoriaz', label: '⛷️ Avoriaz', category: 'Stations de ski' },
  { value: 'les-gets', label: '⛷️ Les Gets', category: 'Stations de ski' },
  { value: 'chatel', label: '⛷️ Châtel', category: 'Stations de ski' },
  { value: 'courchevel-1650', label: '⛷️ Courchevel 1650', category: 'Stations de ski' },
  { value: 'serre-chevalier', label: '⛷️ Serre-Chevalier', category: 'Stations de ski' },
  { value: 'montgenevere', label: '⛷️ Montgenèvre', category: 'Stations de ski' },
  { value: 'la-clusaz', label: '⛷️ La Clusaz', category: 'Stations de ski' },
  { value: 'le-grand-bornand', label: '⛷️ Le Grand-Bornand', category: 'Stations de ski' },
  { value: 'verbier', label: '⛷️ Verbier (Suisse)', category: 'Stations de ski' },
  { value: 'autre', label: '📍 Autre adresse', category: 'Personnalisé' },
];

const destinationLabels: Record<string, string> = {
  'chamonix': '⛷️ Chamonix',
  'megeve': '⛷️ Megève',
  'courchevel': '⛷️ Courchevel',
  'meribel': '⛷️ Méribel',
  'val-thorens': '⛷️ Val Thorens',
  'les-menuires': '⛷️ Les Menuires',
  'val-disere': '⛷️ Val d\'Isère',
  'tignes': '⛷️ Tignes',
  'la-plagne': '⛷️ La Plagne',
  'les-arcs': '⛷️ Les Arcs',
  'morzine': '⛷️ Morzine',
  'annecy': '🏙️ Annecy',
  'geneve': '🏙️ Genève',
  'lyon-aeroport': '✈️ Lyon Aéroport',
  'saint-bon-tarentaise': '⛷️ Saint-Bon-Tarentaise',
  'la-tania': '⛷️ La Tania',
  'les-arcs-1950-2000': '⛷️ Les Arcs (1950/2000)',
  'les-deux-alpes': '⛷️ Les Deux Alpes',
  'la-rosiere': '⛷️ La Rosière (Montvalezan)',
  'alpe-dhuez': '⛷️ Alpe d\'Huez',
  'les-arcs-1600-1800': '⛷️ Les Arcs (1600/1800)',
  'val-claret': '⛷️ Val Claret',
  'les-belleville': '⛷️ Les Belleville',
  'belle-plagne': '⛷️ Belle Plagne',
  'chamrousse': '⛷️ Chamrousse',
  'villard-de-lans': '⛷️ Villard-de-Lans',
  // Aéroports
  'geneve-aeroport': '✈️ Aéroport de Genève (GVA)',
  'lyon-saint-exupery': '✈️ Aéroport Lyon Saint-Exupéry (LYS)',
  'chambery-aeroport': '✈️ Aéroport de Chambéry (CMF)',
  'grenoble-aeroport': '✈️ Aéroport de Grenoble (GNB)',
  'annecy-aeroport': '✈️ Aéroport d\'Annecy',
  'turin-aeroport': '✈️ Aéroport de Turin Caselle (TRN)',
  'milan-malpensa': '✈️ Aéroport de Milan Malpensa (MXP)',
  // Gares
  'gare-moutiers': '🚆 Gare de Moûtiers',
  'gare-albertville': '🚆 Gare TGV Albertville',
  'gare-chambery': '🚆 Gare de Chambéry',
  'gare-annecy-tgv': '🚆 Gare TGV Annecy',
  'gare-geneva-cornavin': '🚆 Gare de Genève Cornavin',
  'gare-lyon-part-dieu': '🚆 Gare de Lyon Part-Dieu',
  'gare-lyon-saint-exupery': '🚆 Gare de Lyon Saint-Exupéry',
  // Hôpitaux
  'hopital-moutiers': '🏥 Hôpital de Moûtiers',
  'hopital-albertville': '🏥 Hôpital d\'Albertville',
  'hopital-bourg-st-maurice': '🏥 Hôpital de Bourg St Maurice',
  'hopital-chambery': '🏥 Hôpital de Chambéry',
  'autre': '📍 Autre destination',
};

export default function ReservationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Réservation Transport Privé | Voyage Elite Premium',
    description: 'Réservez votre transfert privé premium vers les stations de ski. Mercedes Classe E et Van disponibles 24/7.',
    keywords: 'réservation transfert ski, réserver VTC Genève, transport privé Lyon',
    canonical: '/reservation',
    ogTitle: 'Réservation Transport Privé Premium - Voyage Elite',
    ogDescription: 'Réservez votre transfert privé vers les stations de ski. Service 24/7.',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/reservation#webpage`,
          url: `${siteUrl}/reservation`,
          name: 'Réservation Transport Privé Premium',
          description: 'Réservez votre transfert privé premium vers les stations de ski',
          inLanguage: 'fr-FR',
        },
        generateServiceSchema(),
      ],
    },
  });

  const [formData, setFormData] = useState({
    depart: 'geneve-aeroport',
    arrivee: 'chamonix',
    adresseDepart: '',
    adresseArrivee: '',
    date: '',
    heure: '',
    horaireType: 'jour',
    vehicule: 'classe-e',
    passagers: '1',
    bagages: '2',
    skis: 'oui',
    nom: '',
    email: '',
    telephone: '',
    commentaires: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [distance, setDistance] = useState(0);
  const [showCustomDepart, setShowCustomDepart] = useState(false);
  const [showCustomArrivee, setShowCustomArrivee] = useState(false);

  useEffect(() => {
    calculatePrice();
  }, [formData.depart, formData.arrivee, formData.vehicule, formData.horaireType]);

  useEffect(() => {
    setShowCustomDepart(formData.depart === 'autre');
  }, [formData.depart]);

  useEffect(() => {
    setShowCustomArrivee(formData.arrivee === 'autre');
  }, [formData.arrivee]);

  const calculatePrice = () => {
    if (formData.depart === 'autre' || formData.arrivee === 'autre') {
      setCalculatedPrice(0);
      setDistance(0);
      return;
    }

    const routeData = routes[formData.depart]?.[formData.arrivee];
    if (!routeData) {
      setCalculatedPrice(0);
      setDistance(0);
      return;
    }

    let basePrice = routeData.basePrice;
    let nightSurcharge = 0;
    let vanSurcharge = 0;

    // Supplément Van de +25% sur le tarif de base
    if (formData.vehicule === 'van') {
      vanSurcharge = basePrice * 0.25;
    }

    // Supplément nuit de +20% sur le tarif de base (pas sur le total avec van)
    if (formData.horaireType === 'nuit') {
      nightSurcharge = basePrice * 0.20;
    }

    const total = basePrice + vanSurcharge + nightSurcharge;
    setCalculatedPrice(Math.round(total * 100) / 100);
    setDistance(routeData.distance);
  };

  const getAvailableDestinations = () => {
    if (formData.depart === 'autre') {
      return ['autre'];
    }
    const destinations = Object.keys(routes[formData.depart] || {});
    // Ajouter toutes les options de départ comme destinations possibles
    const allOptions = [
      ...destinations,
      'geneve-aeroport',
      'lyon-saint-exupery',
      'chambery-aeroport',
      'grenoble-aeroport',
      'annecy-aeroport',
      'turin-aeroport',
      'milan-malpensa',
      'gare-moutiers',
      'gare-albertville',
      'gare-chambery',
      'gare-annecy-tgv',
      'gare-geneva-cornavin',
      'gare-lyon-part-dieu',
      'gare-lyon-saint-exupery',
      'hopital-moutiers',
      'hopital-albertville',
      'hopital-bourg-st-maurice',
      'hopital-chambery',
      'autre'
    ];
    // Supprimer les doublons
    return [...new Set(allOptions)];
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if (name === 'depart') {
        if (value === 'autre') {
          newData.arrivee = 'autre';
        } else {
          const firstDest = Object.keys(routes[value] || {})[0] || '';
          newData.arrivee = firstDest;
        }
      }
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.depart === 'autre' && !formData.adresseDepart.trim()) {
      alert('Veuillez renseigner votre adresse de départ');
      return;
    }
    
    if (formData.arrivee === 'autre' && !formData.adresseArrivee.trim()) {
      alert('Veuillez renseigner votre adresse d\'arrivée');
      return;
    }

    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      
      const departLabel = formData.depart === 'autre' 
        ? formData.adresseDepart 
        : (departureOptions.find(d => d.value === formData.depart)?.label || formData.depart);
      
      const arriveeLabel = formData.arrivee === 'autre'
        ? formData.adresseArrivee
        : (destinationLabels[formData.arrivee] || formData.arrivee);
      
      formBody.append('depart', departLabel);
      formBody.append('arrivee', arriveeLabel);
      formBody.append('adresseDepart', formData.adresseDepart || departLabel);
      formBody.append('adresseArrivee', formData.adresseArrivee || arriveeLabel);
      formBody.append('date', formData.date);
      formBody.append('heure', formData.heure);
      formBody.append('horaireType', formData.horaireType === 'jour' ? 'Jour (7h-19h)' : 'Nuit (19h-7h) +20%');
      formBody.append('vehicule', formData.vehicule === 'classe-e' ? 'Mercedes Classe E (1-3 pers.)' : 'Mercedes Van (4-7 pers.) +25%');
      formBody.append('passagers', formData.passagers);
      formBody.append('bagages', formData.bagages);
      formBody.append('skis', formData.skis === 'oui' ? 'Oui' : 'Non');
      formBody.append('nom', formData.nom);
      formBody.append('email', formData.email);
      formBody.append('telephone', formData.telephone);
      formBody.append('commentaires', formData.commentaires);
      
      if (calculatedPrice > 0) {
        formBody.append('prixCalcule', `${calculatedPrice}€`);
        formBody.append('distance', `${distance}km`);
      } else {
        formBody.append('prixCalcule', 'Sur devis - Réponse sous 2h');
        formBody.append('distance', 'À calculer');
      }

      const response = await fetch('https://readdy.ai/api/form/d5mgaa0lhqshf1uh0fn0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({
          depart: 'geneve-aeroport',
          arrivee: 'chamonix',
          adresseDepart: '',
          adresseArrivee: '',
          date: '',
          heure: '',
          horaireType: 'jour',
          vehicule: 'classe-e',
          passagers: '1',
          bagages: '2',
          skis: 'oui',
          nom: '',
          email: '',
          telephone: '',
          commentaires: '',
        });
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => setShowSuccess(false), 8000);
      } else {
        alert('Une erreur est survenue. Veuillez réessayer ou nous contacter directement.');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue. Veuillez réessayer ou nous contacter directement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour générer le message WhatsApp
  const generateWhatsAppMessage = () => {
    const departLabel = formData.depart === 'autre' 
      ? formData.adresseDepart 
      : (departureOptions.find(d => d.value === formData.depart)?.label || formData.depart);
    
    const arriveeLabel = formData.arrivee === 'autre'
      ? formData.adresseArrivee
      : (destinationLabels[formData.arrivee] || formData.arrivee);

    const vehiculeLabel = formData.vehicule === 'classe-e' 
      ? 'Mercedes Classe E (1-3 pers.)' 
      : 'Mercedes Van (4-7 pers.)';

    const horaireLabel = formData.horaireType === 'jour' 
      ? 'Jour (7h-19h)' 
      : 'Nuit (19h-7h)';

    let message = `🚗 *Demande de réservation*\n\n`;
    message += `📍 *Départ :* ${departLabel}\n`;
    if (formData.adresseDepart && formData.depart !== 'autre') {
      message += `   ↳ ${formData.adresseDepart}\n`;
    }
    message += `📍 *Arrivée :* ${arriveeLabel}\n`;
    if (formData.adresseArrivee && formData.arrivee !== 'autre') {
      message += `   ↳ ${formData.adresseArrivee}\n`;
    }
    message += `\n📅 *Date :* ${formData.date || 'À définir'}\n`;
    message += `🕐 *Heure :* ${formData.heure || 'À définir'}\n`;
    message += `⏰ *Horaire :* ${horaireLabel}\n`;
    message += `\n🚙 *Véhicule :* ${vehiculeLabel}\n`;
    message += `👥 *Passagers :* ${formData.passagers}\n`;
    message += `🧳 *Bagages :* ${formData.bagages}\n`;
    message += `⛷️ *Équipement ski :* ${formData.skis === 'oui' ? 'Oui' : 'Non'}\n`;
    
    if (calculatedPrice > 0) {
      message += `\n💰 *Prix estimé :* ${calculatedPrice}€ (${distance}km)\n`;
    } else {
      message += `\n💰 *Prix :* Sur devis\n`;
    }

    if (formData.nom) {
      message += `\n👤 *Nom :* ${formData.nom}\n`;
    }
    if (formData.telephone) {
      message += `📞 *Téléphone :* ${formData.telephone}\n`;
    }
    if (formData.email) {
      message += `📧 *Email :* ${formData.email}\n`;
    }

    if (formData.commentaires) {
      message += `\n💬 *Commentaires :*\n${formData.commentaires}\n`;
    }

    return encodeURIComponent(message);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Navbar />
      
      <div className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-20 lg:pb-24 px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 sm:mb-10"
          >
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-night-blue mb-3 sm:mb-4">
              Réservation Simple
            </h1>
            <p className="text-slate-600 font-inter text-base sm:text-lg">
              Sélectionnez votre trajet et réservez en quelques clics
            </p>
          </motion.div>

          <BookingTracker />
          <AvailabilityCalendar />

          <AnimatePresence>
            {showSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                className="mb-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-2xl overflow-hidden"
              >
                <div className="p-6 sm:p-8 text-white">
                  <div className="flex items-center gap-4 mb-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring' }}
                      className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center"
                    >
                      <i className="ri-check-line text-4xl"></i>
                    </motion.div>
                    <div>
                      <h3 className="font-playfair text-2xl font-bold mb-1">Réservation confirmée !</h3>
                      <p className="text-green-50">Nous vous contacterons sous 2h</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <AnimatePresence mode="wait">
              {calculatedPrice > 0 && (
                <motion.div
                  key={calculatedPrice}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-gradient-to-br from-alpine-gold via-amber-500 to-alpine-gold p-8 relative overflow-hidden"
                >
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse"></div>
                  </div>
                  
                  <div className="relative z-10 text-center">
                    <motion.div
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200 }}
                    >
                      <div className="inline-flex items-center gap-2 bg-night-blue/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                        <span className="text-night-blue font-inter text-sm font-bold">Prix calculé instantanément</span>
                      </div>
                      
                      {/* Détail du calcul */}
                      {(() => {
                        const routeData = routes[formData.depart]?.[formData.arrivee];
                        if (!routeData) return null;
                        
                        const basePrice = routeData.basePrice;
                        const vanSurcharge = formData.vehicule === 'van' ? basePrice * 0.25 : 0;
                        const nightSurcharge = formData.horaireType === 'nuit' ? basePrice * 0.20 : 0;
                        
                        const hasSupplements = nightSurcharge > 0 || vanSurcharge > 0;
                        
                        return (
                          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-4 max-w-md mx-auto">
                            <div className="space-y-3 text-left">
                              <div className="flex justify-between items-center text-night-blue">
                                <span className="font-inter font-medium">Tarif de base</span>
                                <span className="font-inter font-bold">{basePrice.toFixed(2)}€</span>
                              </div>
                              
                              {vanSurcharge > 0 && (
                                <div className="flex justify-between items-center text-night-blue">
                                  <span className="font-inter font-medium">Supplément Van (+25%)</span>
                                  <span className="font-inter font-bold text-amber-700">+{vanSurcharge.toFixed(2)}€</span>
                                </div>
                              )}
                              
                              {nightSurcharge > 0 && (
                                <div className="flex justify-between items-center text-night-blue">
                                  <span className="font-inter font-medium">Supplément nuit (+20%)</span>
                                  <span className="font-inter font-bold text-amber-700">+{nightSurcharge.toFixed(2)}€</span>
                                </div>
                              )}
                              
                              {hasSupplements && (
                                <div className="border-t-2 border-night-blue/20 pt-3 mt-3"></div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                      
                      <div className="font-playfair text-6xl md:text-7xl font-bold text-night-blue mb-2">
                        {calculatedPrice}€
                      </div>
                      <div className="text-night-blue/80 font-inter text-lg font-semibold mb-6">
                        {distance}km • Prix fixe tout compris
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <form id="reservation-form" onSubmit={handleSubmit} data-readdy-form className="p-6 sm:p-8 lg:p-12">
              {/* Sélection du trajet */}
              <div className="mb-8">
                <h2 className="font-playfair text-2xl font-bold text-night-blue mb-6 flex items-center gap-3">
                  <i className="ri-route-line text-alpine-gold text-3xl"></i>
                  Votre trajet
                </h2>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-night-blue font-inter font-semibold mb-3">
                      Point de départ
                    </label>
                    <select
                      name="depart"
                      value={formData.depart}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-4 border-2 border-slate-300 rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-4 focus:ring-alpine-gold/20 transition-all cursor-pointer bg-white"
                    >
                      <optgroup label="✈️ Aéroports">
                        {departureOptions.filter(opt => opt.category === 'Aéroports').map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="🚆 Gares">
                        {departureOptions.filter(opt => opt.category === 'Gares').map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="🏥 Hôpitaux">
                        {departureOptions.filter(opt => opt.category === 'Hôpitaux').map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="⛷️ Stations de ski">
                        {departureOptions.filter(opt => opt.category === 'Stations de ski').map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="📍 Personnalisé">
                        {departureOptions.filter(opt => opt.category === 'Personnalisé').map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    
                    {showCustomDepart && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3"
                      >
                        <input
                          type="text"
                          name="adresseDepart"
                          value={formData.adresseDepart}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-alpine-gold rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20 transition-all"
                          placeholder="Entrez votre adresse de départ complète"
                        />
                      </motion.div>
                    )}
                  </div>

                  <div>
                    <label className="block text-night-blue font-inter font-semibold mb-3">
                      Destination
                    </label>
                    <select
                      name="arrivee"
                      value={formData.arrivee}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-4 border-2 border-slate-300 rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-4 focus:ring-alpine-gold/20 transition-all cursor-pointer bg-white"
                    >
                      <optgroup label="⛷️ Stations de ski">
                        {getAvailableDestinations().filter(dest => 
                          ['chamonix', 'megeve', 'courchevel', 'meribel', 'val-thorens', 'les-menuires', 
                           'val-disere', 'tignes', 'la-plagne', 'les-arcs', 'morzine', 'saint-bon-tarentaise',
                           'la-tania', 'les-arcs-1950-2000', 'les-deux-alpes', 'la-rosiere', 'alpe-dhuez',
                           'les-arcs-1600-1800', 'val-claret', 'les-belleville', 'belle-plagne', 'chamrousse',
                           'villard-de-lans', 'flaine', 'avoriaz', 'les-gets', 'chatel', 'courchevel-1650',
                           'serre-chevalier', 'montgenevere', 'la-clusaz', 'le-grand-bornand', 'verbier'].includes(dest)
                        ).map(dest => (
                          <option key={dest} value={dest}>
                            {destinationLabels[dest] || dest}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="✈️ Aéroports">
                        {getAvailableDestinations().filter(dest => 
                          ['geneve-aeroport', 'lyon-saint-exupery', 'chambery-aeroport', 'grenoble-aeroport',
                           'annecy-aeroport', 'turin-aeroport', 'milan-malpensa'].includes(dest)
                        ).map(dest => (
                          <option key={dest} value={dest}>
                            {destinationLabels[dest] || dest}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="🚆 Gares">
                        {getAvailableDestinations().filter(dest => 
                          ['gare-moutiers', 'gare-albertville', 'gare-chambery', 'gare-annecy-tgv',
                           'gare-geneva-cornavin', 'gare-lyon-part-dieu', 'gare-lyon-saint-exupery'].includes(dest)
                        ).map(dest => (
                          <option key={dest} value={dest}>
                            {destinationLabels[dest] || dest}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="🏥 Hôpitaux">
                        {getAvailableDestinations().filter(dest => 
                          ['hopital-moutiers', 'hopital-albertville', 'hopital-bourg-st-maurice', 'hopital-chambery'].includes(dest)
                        ).map(dest => (
                          <option key={dest} value={dest}>
                            {destinationLabels[dest] || dest}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="🏙️ Villes">
                        {getAvailableDestinations().filter(dest => 
                          ['annecy', 'geneve'].includes(dest)
                        ).map(dest => (
                          <option key={dest} value={dest}>
                            {destinationLabels[dest] || dest}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="📍 Personnalisé">
                        {getAvailableDestinations().filter(dest => dest === 'autre').map(dest => (
                          <option key={dest} value={dest}>
                            {destinationLabels[dest] || dest}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    
                    {showCustomArrivee && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3"
                      >
                        <input
                          type="text"
                          name="adresseArrivee"
                          value={formData.adresseArrivee}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border-2 border-alpine-gold rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20 transition-all"
                          placeholder="Entrez votre destination complète"
                        />
                      </motion.div>
                    )}
                  </div>
                </div>

                {!showCustomDepart && !showCustomArrivee && (
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-night-blue font-inter font-medium mb-2">
                        Adresse précise de départ (optionnel)
                      </label>
                      <input
                        type="text"
                        name="adresseDepart"
                        value={formData.adresseDepart}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20 transition-all"
                        placeholder="Numéro de vol, terminal..."
                      />
                    </div>

                    <div>
                      <label className="block text-night-blue font-inter font-medium mb-2">
                        Adresse précise d'arrivée (optionnel)
                      </label>
                      <input
                        type="text"
                        name="adresseArrivee"
                        value={formData.adresseArrivee}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20 transition-all"
                        placeholder="Nom de l'hôtel, chalet..."
                      />
                    </div>
                  </div>
                )}

                {(showCustomDepart || showCustomArrivee) && (
                  <div className="bg-amber-50 border-2 border-alpine-gold/30 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line text-alpine-gold text-xl flex-shrink-0 mt-0.5"></i>
                      <div className="text-sm text-slate-700 font-inter">
                        <strong className="text-night-blue">Trajet personnalisé</strong>
                        <p className="mt-1">Le prix sera calculé et communiqué par notre équipe dans les 2 heures.</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-night-blue font-inter font-semibold mb-3">
                      Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-4 border-2 border-slate-300 rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-4 focus:ring-alpine-gold/20 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-night-blue font-inter font-semibold mb-3">
                      Heure
                    </label>
                    <input
                      type="time"
                      name="heure"
                      value={formData.heure}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-4 border-2 border-slate-300 rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-4 focus:ring-alpine-gold/20 transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Options */}
              <div className="mb-8">
                <h2 className="font-playfair text-2xl font-bold text-night-blue mb-6 flex items-center gap-3">
                  <i className="ri-settings-3-line text-alpine-gold text-3xl"></i>
                  Options
                </h2>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-night-blue font-inter font-semibold mb-3">
                      Horaire
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="relative cursor-pointer">
                        <input
                          type="radio"
                          name="horaireType"
                          value="jour"
                          checked={formData.horaireType === 'jour'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div className={`p-4 border-2 rounded-xl transition-all text-center ${
                          formData.horaireType === 'jour' 
                            ? 'border-alpine-gold bg-amber-50 shadow-lg' 
                            : 'border-slate-300 bg-white'
                        }`}>
                          <i className="ri-sun-line text-2xl mb-2 text-alpine-gold"></i>
                          <div className="font-inter font-bold text-sm">Jour</div>
                        </div>
                      </label>

                      <label className="relative cursor-pointer">
                        <input
                          type="radio"
                          name="horaireType"
                          value="nuit"
                          checked={formData.horaireType === 'nuit'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div className={`p-4 border-2 rounded-xl transition-all text-center ${
                          formData.horaireType === 'nuit' 
                            ? 'border-alpine-gold bg-amber-50 shadow-lg' 
                            : 'border-slate-300 bg-white'
                        }`}>
                          <i className="ri-moon-line text-2xl mb-2 text-alpine-gold"></i>
                          <div className="font-inter font-bold text-sm">Nuit +20%</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-night-blue font-inter font-semibold mb-3">
                      Véhicule
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="relative cursor-pointer">
                        <input
                          type="radio"
                          name="vehicule"
                          value="classe-e"
                          checked={formData.vehicule === 'classe-e'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div className={`p-4 border-2 rounded-xl transition-all text-center ${
                          formData.vehicule === 'classe-e' 
                            ? 'border-alpine-gold bg-amber-50 shadow-lg' 
                            : 'border-slate-300 bg-white'
                        }`}>
                          <i className="ri-car-line text-2xl mb-2 text-alpine-gold"></i>
                          <div className="font-inter font-bold text-sm">Classe E</div>
                          <div className="text-xs text-slate-600">1-3 pers.</div>
                        </div>
                      </label>

                      <label className="relative cursor-pointer">
                        <input
                          type="radio"
                          name="vehicule"
                          value="van"
                          checked={formData.vehicule === 'van'}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div className={`p-4 border-2 rounded-xl transition-all text-center ${
                          formData.vehicule === 'van' 
                            ? 'border-alpine-gold bg-amber-50 shadow-lg' 
                            : 'border-slate-300 bg-white'
                        }`}>
                          <i className="ri-truck-line text-2xl mb-2 text-alpine-gold"></i>
                          <div className="font-inter font-bold text-sm">Van +25%</div>
                          <div className="text-xs text-slate-600">4-7 pers.</div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-night-blue font-inter font-medium mb-2">
                      Passagers
                    </label>
                    <select
                      name="passagers"
                      value={formData.passagers}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl font-inter focus:outline-none focus:border-alpine-gold transition-all cursor-pointer bg-white"
                    >
                      {[1, 2, 3, 4, 5, 6, 7].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-night-blue font-inter font-medium mb-2">
                      Bagages
                    </label>
                    <select
                      name="bagages"
                      value={formData.bagages}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl font-inter focus:outline-none focus:border-alpine-gold transition-all cursor-pointer bg-white"
                    >
                      {[0, 1, 2, 3, 4, 5, 6].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-night-blue font-inter font-medium mb-2">
                      Skis
                    </label>
                    <select
                      name="skis"
                      value={formData.skis}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl font-inter focus:outline-none focus:border-alpine-gold transition-all cursor-pointer bg-white"
                    >
                      <option value="oui">Oui</option>
                      <option value="non">Non</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Vos informations */}
              <div className="mb-8">
                <h2 className="font-playfair text-2xl font-bold text-night-blue mb-6 flex items-center gap-3">
                  <i className="ri-user-line text-alpine-gold text-3xl"></i>
                  Vos informations
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-night-blue font-inter font-medium mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20 transition-all"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-night-blue font-inter font-medium mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20 transition-all"
                        placeholder="votre@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-night-blue font-inter font-medium mb-2">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20 transition-all"
                        placeholder="+33 / +41"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-night-blue font-inter font-medium mb-2">
                      Commentaires
                    </label>
                    <textarea
                      name="commentaires"
                      value={formData.commentaires}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl font-inter text-base focus:outline-none focus:border-alpine-gold focus:ring-2 focus:ring-alpine-gold/20 transition-all resize-none"
                      placeholder="Siège bébé, arrêt en route, numéro de vol..."
                    />
                    <p className="text-slate-500 text-sm mt-2">
                      {formData.commentaires.length}/500 caractères
                    </p>
                  </div>
                </div>
              </div>

              {/* Bouton de soumission */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-alpine-gold to-amber-600 text-night-blue py-5 rounded-xl font-inter font-bold text-lg hover:from-amber-600 hover:to-alpine-gold transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center justify-center gap-3 shadow-xl"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-2xl"></i>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <i className="ri-check-double-line text-2xl"></i>
                    {calculatedPrice > 0 ? `Confirmer ma réservation • ${calculatedPrice}€` : 'Demander un devis gratuit'}
                  </>
                )}
              </motion.button>

              <div className="mt-6 text-center">
                <p className="text-slate-600 text-sm">
                  <i className="ri-shield-check-line text-green-500 mr-2"></i>
                  {calculatedPrice > 0 ? 'Paiement sécurisé • Confirmation immédiate • Annulation gratuite' : 'Devis gratuit sous 2h • Sans engagement'}
                </p>
              </div>

              {/* Bouton WhatsApp */}
              <motion.a
                href={`https://wa.me/33605606006?text=${generateWhatsAppMessage()}`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-4 w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-5 rounded-xl font-inter font-bold text-lg hover:from-green-600 hover:to-green-700 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-3 shadow-xl"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                Réserver via WhatsApp
              </motion.a>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {[
              { icon: 'ri-time-line', text: 'Service 24/7' },
              { icon: 'ri-shield-check-line', text: 'Assurance incluse' },
              { icon: 'ri-user-star-line', text: 'Chauffeurs pros' },
              { icon: 'ri-price-tag-3-line', text: 'Prix fixes' }
            ].map((item, index) => (
              <div key={index} className="bg-white p-4 rounded-xl shadow-md text-center">
                <i className={`${item.icon} text-alpine-gold text-2xl mb-2`}></i>
                <div className="text-night-blue font-inter font-bold text-sm">{item.text}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}